import React, { useCallback, useEffect, useMemo, useRef } from "react";
import type {
  PopRenderPayload,
  PopupGetter,
  useRenderPopupOptions,
} from "scmp-sdk/components/acquisition-popup-container/display/types";
import { useSdkContext } from "scmp-sdk/components/contexts";

import { MyNewsCompletionPopup } from ".";

export const useMyNewsCompletionPopupGetter = (options: useRenderPopupOptions): PopupGetter => {
  const {
    acquisition: { popupState },
    handleTrackGA4,
    hasEnoughFollowedItems,
    initialHasEnoughFollowedItems,
  } = useSdkContext();

  const needToDisplay = useMemo(
    () =>
      // 1. Drupal Config is on
      popupState.myNews.config?.completion_popup?.isEnabled &&
      // 2. Is Logged in
      options.isLoggedIn &&
      // 3. Followed items length is larger than threshold
      hasEnoughFollowedItems &&
      // 4. Snapshot list length is not larger than threshold (So if the user already followed more than threshold in the beginning, the popup will not show)
      !initialHasEnoughFollowedItems,
    [
      hasEnoughFollowedItems,
      initialHasEnoughFollowedItems,
      options.isLoggedIn,
      popupState.myNews.config?.completion_popup?.isEnabled,
    ],
  );

  const getNextFromConfig = useCallback(
    (handleClose: () => void): PopRenderPayload | undefined => {
      if (!popupState.myNews?.completion || !needToDisplay) return;
      const localStorageKey = popupState.myNews?.completion?.localStorageKey;
      const hasAlreadyShown = localStorage.getItem(localStorageKey) !== null;
      if (hasAlreadyShown) return;

      const props = {
        config: popupState.myNews?.completion,
        modalContainerReference: options.modalContainerReference,
        onClose: handleClose,
      };
      return {
        element: <MyNewsCompletionPopup {...props} />,
        localStorageKey,
      };
    },
    [needToDisplay, options.modalContainerReference, popupState.myNews?.completion],
  );

  const isGASent = useRef(false);
  useEffect(() => {
    if (isGASent.current) return;
    if (needToDisplay) {
      handleTrackGA4({
        action: "sys",
        category: "mynews",
        customized_parameters: {
          trigger_point: "mynews_setup",
        },
        subcategory: "completion",
      });
      isGASent.current = true;
    }
  }, [needToDisplay, handleTrackGA4]);

  const getters = useMemo(() => ({ getNextFromConfig }), [getNextFromConfig]);

  return getters;
};
